import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useTask } from '../contexts/TaskContext';
import { useAuth } from '../../../hooks/useAuth';
import { collectionGroup, query, getDocs, doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../services/firebase';
import { format, subDays, isAfter, isBefore, isToday, startOfDay, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { FiInbox, FiStar, FiArchive, FiZap, FiCheckCircle, FiAlertCircle, FiClock, FiTrendingUp, FiChevronRight, FiCalendar, FiUser, FiActivity } from 'react-icons/fi';
import { Sparkles, Star, Archive, MessageCircle, GripVertical } from 'lucide-react';
import toast from 'react-hot-toast';
import GlobalChat from '../../../components/ui/GlobalChat';

const parseDate = (d) => {
    if (!d) return null;
    if (typeof d === 'object' && d.seconds) return new Date(d.seconds * 1000);
    if (typeof d === 'string') return new Date(d);
    return null;
};

// ── AI Summary Card ──
const AISummaryCard = ({ tasks, user }) => {
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [collapsed, setCollapsed] = useState(false);

    const generateSummary = () => {
        const now = new Date();
        const myTasks = tasks.filter(t => t.asignados?.includes(user?.email));
        const overdue = myTasks.filter(t => {
            const d = parseDate(t.fechaVencimiento);
            return d && isBefore(d, startOfDay(now)) && t.estado !== 'finalizado';
        });
        const dueToday = myTasks.filter(t => {
            const d = parseDate(t.fechaVencimiento);
            return d && isToday(d) && t.estado !== 'finalizado';
        });
        const completedRecent = myTasks.filter(t => t.estado === 'finalizado').slice(0, 5);
        const inProgress = myTasks.filter(t => t.estado === 'en-curso' || t.estado === 'en_curso');
        const pending = myTasks.filter(t => t.estado !== 'finalizado');

        setSummary({
            total: myTasks.length,
            overdue: overdue.length,
            overdueItems: overdue.slice(0, 3),
            dueToday: dueToday.length,
            dueTodayItems: dueToday.slice(0, 3),
            inProgress: inProgress.length,
            completed: completedRecent.length,
            pending: pending.length,
            urgentMessage: overdue.length > 0
                ? `Tienes ${overdue.length} tarea${overdue.length > 1 ? 's' : ''} vencida${overdue.length > 1 ? 's' : ''} que necesitan atención inmediata.`
                : dueToday.length > 0
                    ? `Hoy vencen ${dueToday.length} tarea${dueToday.length > 1 ? 's' : ''}. ¡A darle!`
                    : `Todo al día. ${pending.length} tarea${pending.length !== 1 ? 's' : ''} pendiente${pending.length !== 1 ? 's' : ''}.`,
        });
    };

    useEffect(() => { if (tasks.length > 0) generateSummary(); }, [tasks, user]);

    if (!summary || collapsed) {
        return (
            <button onClick={() => setCollapsed(false)}
                className="w-full p-3 bg-gradient-to-r from-violet-50 to-blue-50 border border-violet-200 rounded-xl flex items-center gap-2 text-sm text-violet-700 hover:from-violet-100 transition-all">
                <Sparkles size={16} /> Ver resumen inteligente
            </button>
        );
    }

    return (
        <div className="bg-gradient-to-br from-violet-50 via-blue-50 to-indigo-50 border border-violet-200/60 rounded-2xl p-5 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-violet-200/20 rounded-full blur-2xl -mr-10 -mt-10" />
            <div className="flex items-center justify-between mb-3 relative">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-violet-600 rounded-lg flex items-center justify-center">
                        <Sparkles size={16} className="text-white" />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-gray-800">Resumen inteligente</h3>
                        <p className="text-[10px] text-gray-500">Hoy, {format(new Date(), "d 'de' MMMM", { locale: es })}</p>
                    </div>
                </div>
                <button onClick={() => setCollapsed(true)} className="text-gray-400 hover:text-gray-600 text-xs">Ocultar</button>
            </div>

            <p className="text-sm text-gray-700 mb-4 relative font-medium">{summary.urgentMessage}</p>

            <div className="grid grid-cols-4 gap-2 relative">
                {[
                    { label: 'Vencidas', value: summary.overdue, color: 'text-red-600 bg-red-50 border-red-200', icon: FiAlertCircle },
                    { label: 'Hoy', value: summary.dueToday, color: 'text-blue-600 bg-blue-50 border-blue-200', icon: FiClock },
                    { label: 'En curso', value: summary.inProgress, color: 'text-amber-600 bg-amber-50 border-amber-200', icon: FiTrendingUp },
                    { label: 'Completadas', value: summary.completed, color: 'text-green-600 bg-green-50 border-green-200', icon: FiCheckCircle },
                ].map((s, i) => (
                    <div key={i} className={`rounded-xl p-3 border text-center ${s.color}`}>
                        <s.icon className="mx-auto mb-1" size={16} />
                        <p className="text-lg font-bold">{s.value}</p>
                        <p className="text-[10px] font-medium uppercase">{s.label}</p>
                    </div>
                ))}
            </div>

            {summary.overdueItems.length > 0 && (
                <div className="mt-3 pt-3 border-t border-violet-200/40">
                    <p className="text-[10px] font-bold text-red-500 uppercase mb-1.5">Requieren atención</p>
                    {summary.overdueItems.map((t, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-gray-700 py-1">
                            <FiAlertCircle size={11} className="text-red-400 shrink-0" />
                            <span className="truncate">{t.titulo}</span>
                            {parseDate(t.fechaVencimiento) && (
                                <span className="text-red-500 text-[10px] ml-auto shrink-0">{format(parseDate(t.fechaVencimiento), 'd MMM', { locale: es })}</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

// ── Activity Item ──
const ActivityItem = ({ activity, users, onTaskClick }) => {
    const u = users?.find(u => u.email === activity.userEmail || u.id === activity.userId);
    const date = parseDate(activity.timestamp || activity.fechaCreacion);

    return (
        <div className="flex items-start gap-3 py-3 px-1 border-b border-gray-100 hover:bg-gray-50/50 transition-colors cursor-pointer"
            onClick={() => onTaskClick && onTaskClick(activity.taskId)}>
            <div className="shrink-0 mt-0.5">
                {u?.photoURL ? (
                    <img src={u.photoURL} alt="" className="w-8 h-8 rounded-full object-cover" />
                ) : (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600">
                        {(u?.displayName || activity.userEmail || '?').charAt(0).toUpperCase()}
                    </div>
                )}
            </div>
            <div className="flex-1 min-w-0">
                <p className="text-[13px] text-gray-700">
                    <b className="text-gray-900">{u?.displayName || activity.userEmail || 'Usuario'}</b>
                    {' '}{activity.description || activity.action || 'realizó una acción'}
                </p>
                {activity.taskTitle && (
                    <p className="text-xs text-blue-600 font-medium mt-0.5 truncate">{activity.taskTitle}</p>
                )}
                {date && (
                    <p className="text-[10px] text-gray-400 mt-0.5">{format(date, "d MMM, HH:mm", { locale: es })}</p>
                )}
            </div>
        </div>
    );
};

// ── Favorite Task Card ──
const FavoriteCard = ({ task, onRemove, onTaskClick }) => {
    const date = parseDate(task.fechaVencimiento);
    const isOverdue = date && isBefore(date, startOfDay(new Date())) && task.estado !== 'finalizado';

    return (
        <div className="bg-white border border-gray-200 rounded-xl p-4 hover:shadow-md transition-all cursor-pointer group"
            onClick={() => onTaskClick(task.id)}>
            <div className="flex items-start justify-between mb-2">
                <div className={`w-2 h-2 rounded-full mt-1.5 shrink-0 ${
                    task.estado === 'finalizado' ? 'bg-green-500' : isOverdue ? 'bg-red-500' : 'bg-blue-500'
                }`} />
                <button onClick={(e) => { e.stopPropagation(); onRemove(task.id); }}
                    className="p-1 text-amber-400 hover:text-gray-400 opacity-0 group-hover:opacity-100 transition-all" title="Quitar de favoritos">
                    <Star size={14} fill="currentColor" />
                </button>
            </div>
            <h4 className={`text-sm font-medium mb-2 ${task.estado === 'finalizado' ? 'text-gray-400 line-through' : 'text-gray-800'}`}>
                {task.titulo}
            </h4>
            <div className="flex items-center gap-2 text-[10px] text-gray-400">
                {date && (
                    <span className={`flex items-center gap-0.5 ${isOverdue ? 'text-red-500' : ''}`}>
                        <FiCalendar size={9} /> {format(date, 'd MMM', { locale: es })}
                    </span>
                )}
                <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-medium ${
                    task.estado === 'finalizado' ? 'bg-green-100 text-green-600' :
                    task.estado === 'en-curso' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-500'
                }`}>
                    {task.estado === 'finalizado' ? 'Completada' : task.estado === 'en-curso' ? 'En curso' : 'Pendiente'}
                </span>
            </div>
        </div>
    );
};

// ══════════════════════════════════
// MAIN INBOX PAGE
// ══════════════════════════════════
const InboxPage = () => {
    const { user } = useAuth();
    const { tasks, users, setSelectedTaskId, setShowTaskDrawer, setSelectedSubtaskPath } = useTask();
    const [activeTab, setActiveTab] = useState('actividad');
    const [favorites, setFavorites] = useState([]);
    const [activities, setActivities] = useState([]);
    const [chatWidth, setChatWidth] = useState(() => {
        const saved = localStorage.getItem('inboxChatWidth');
        return saved ? parseInt(saved) : 360;
    });
    const [resizing, setResizing] = useState(false);
    const containerRef = useRef(null);

    // Load favorites from Firestore
    useEffect(() => {
        if (!user?.uid) return;
        const ref = doc(db, `users/${user.uid}/preferences/favorites`);
        const unsub = onSnapshot(ref, (snap) => {
            if (snap.exists()) setFavorites(snap.data().taskIds || []);
            else setFavorites([]);
        });
        return unsub;
    }, [user?.uid]);

    // Collect activities from tasks (last 7 days)
    useEffect(() => {
        if (!tasks || tasks.length === 0) return;
        const sevenDaysAgo = subDays(new Date(), 7);
        const allActivities = [];

        tasks.forEach(task => {
            if (task.activityLog) {
                task.activityLog.forEach(log => {
                    const date = parseDate(log.timestamp);
                    if (date && isAfter(date, sevenDaysAgo)) {
                        allActivities.push({
                            ...log,
                            taskId: task.id,
                            taskTitle: task.titulo,
                        });
                    }
                });
            }
            // Also add task creation as activity
            const created = parseDate(task.fechaCreacion);
            if (created && isAfter(created, sevenDaysAgo)) {
                allActivities.push({
                    taskId: task.id,
                    taskTitle: task.titulo,
                    description: 'creó esta tarea',
                    userId: task.creadoPor,
                    userEmail: task.creadoPor,
                    timestamp: task.fechaCreacion,
                });
            }
        });

        allActivities.sort((a, b) => {
            const da = parseDate(a.timestamp);
            const db2 = parseDate(b.timestamp);
            return (db2?.getTime() || 0) - (da?.getTime() || 0);
        });

        setActivities(allActivities.slice(0, 50));
    }, [tasks]);

    const toggleFavorite = async (taskId) => {
        if (!user?.uid) return;
        const ref = doc(db, `users/${user.uid}/preferences/favorites`);
        const newFavs = favorites.includes(taskId)
            ? favorites.filter(id => id !== taskId)
            : [...favorites, taskId];
        await setDoc(ref, { taskIds: newFavs }, { merge: true });
    };

    const favoriteTasks = useMemo(() => {
        return favorites.map(id => tasks.find(t => t.id === id)).filter(Boolean);
    }, [favorites, tasks]);

    const handleTaskClick = (taskId) => {
        setSelectedTaskId(taskId);
        setSelectedSubtaskPath([]);
        setShowTaskDrawer(true);
    };

    // Resize handler for chat panel
    const handleResizeStart = (e) => {
        e.preventDefault();
        setResizing(true);
        const startX = e.clientX;
        const startWidth = chatWidth;
        const handleMove = (e) => {
            const diff = startX - e.clientX;
            const newWidth = Math.max(280, Math.min(600, startWidth + diff));
            setChatWidth(newWidth);
        };
        const handleUp = () => {
            setResizing(false);
            localStorage.setItem('inboxChatWidth', String(chatWidth));
            document.removeEventListener('mousemove', handleMove);
            document.removeEventListener('mouseup', handleUp);
        };
        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleUp);
    };

    const tabs = [
        { id: 'actividad', label: 'Actividad', icon: FiActivity },
        { id: 'favoritos', label: 'Favoritos', icon: FiStar, count: favorites.length },
        { id: 'archivo', label: 'Archivo', icon: FiArchive },
    ];

    // Group activities by day
    const groupedActivities = useMemo(() => {
        const groups = {};
        const today = startOfDay(new Date());
        const yesterday = subDays(today, 1);

        activities.forEach(a => {
            const d = parseDate(a.timestamp);
            if (!d) return;
            let key;
            if (isAfter(d, today)) key = 'Hoy';
            else if (isAfter(d, yesterday)) key = 'Ayer';
            else key = format(d, "EEEE d 'de' MMMM", { locale: es });
            if (!groups[key]) groups[key] = [];
            groups[key].push(a);
        });
        return groups;
    }, [activities]);

    return (
        <div ref={containerRef} className="flex h-full overflow-hidden">
            {/* Main content */}
            <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
                {/* Header */}
                <div className="px-6 pt-5 pb-0 bg-white border-b border-gray-200">
                    <h1 className="text-xl font-bold text-gray-900 mb-4">Bandeja de entrada</h1>
                    <div className="flex items-center gap-0.5">
                        {tabs.map(tab => (
                            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                                className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 transition-colors -mb-px ${
                                    activeTab === tab.id
                                        ? 'border-blue-600 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}>
                                <tab.icon size={14} />
                                {tab.label}
                                {tab.count > 0 && (
                                    <span className="text-[10px] bg-blue-100 text-blue-600 px-1.5 py-0.5 rounded-full font-bold">{tab.count}</span>
                                )}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Tab content */}
                <div className="flex-1 overflow-y-auto bg-gray-50">
                    {activeTab === 'actividad' && (
                        <div className="max-w-3xl mx-auto p-6 space-y-5">
                            <AISummaryCard tasks={tasks} user={user} />

                            {Object.keys(groupedActivities).length === 0 ? (
                                <div className="text-center py-12 text-gray-400">
                                    <FiInbox size={40} className="mx-auto mb-3 opacity-30" />
                                    <p className="text-sm">No hay actividad reciente</p>
                                </div>
                            ) : (
                                Object.entries(groupedActivities).map(([day, acts]) => (
                                    <div key={day}>
                                        <h3 className="text-xs font-bold text-gray-500 uppercase mb-2 px-1">{day}</h3>
                                        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                                            {acts.map((a, i) => (
                                                <ActivityItem key={i} activity={a} users={users} onTaskClick={handleTaskClick} />
                                            ))}
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {activeTab === 'favoritos' && (
                        <div className="max-w-3xl mx-auto p-6">
                            {favoriteTasks.length === 0 ? (
                                <div className="text-center py-16 text-gray-400">
                                    <Star size={40} className="mx-auto mb-3 opacity-30" />
                                    <p className="text-sm mb-1">Sin favoritos</p>
                                    <p className="text-xs text-gray-400">Marca tareas con ★ para verlas aquí</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                    {favoriteTasks.map(task => (
                                        <FavoriteCard key={task.id} task={task} onRemove={toggleFavorite} onTaskClick={handleTaskClick} />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'archivo' && (
                        <div className="max-w-3xl mx-auto p-6 text-center py-16 text-gray-400">
                            <Archive size={40} className="mx-auto mb-3 opacity-30" />
                            <p className="text-sm">Archivo vacío</p>
                            <p className="text-xs">Las notificaciones archivadas aparecerán aquí</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Resize handle */}
            <div className="w-1 cursor-col-resize hover:bg-blue-400 bg-gray-200 transition-colors shrink-0 relative group"
                onMouseDown={handleResizeStart}>
                <div className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 w-4 h-8 rounded bg-gray-300 group-hover:bg-blue-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <GripVertical size={10} className="text-white" />
                </div>
            </div>

            {/* Chat panel (embedded GlobalChat) */}
            <div style={{ width: chatWidth }} className="shrink-0 bg-white flex flex-col h-full overflow-hidden">
                <GlobalChat embedded />
            </div>
        </div>
    );
};

export default InboxPage;
